/**
 * Canonical item category color tokens.
 *
 * Single source of truth for every surface that renders an item category label
 * (badge, dot, chart bar, tile). Import from here instead of defining local
 * color constants.
 *
 * ── The invariant ──────────────────────────────────────────────────────────
 * ONE Tailwind family per category, used by the badge, the dot and the hex.
 * Every ramp then maps family → hue in one place (the accent table in
 * scripts/buildLightOverrides.mjs), so a chip painted by a class and a chart
 * bar painted by a hex cannot disagree on any screen. Adding a per-surface
 * colour is the defect this file exists to prevent.
 *
 * Canonical palette (chosen for mutual contrast):
 *   risk        → red       hue   5°
 *   problem     → amber     hue  41°
 *   solution    → lime      hue  85°
 *   investment  → emerald   hue 155°
 *   opportunity → blue      hue 214°
 *   commitment  → violet    hue 279°
 *
 * solution has now moved twice, and the reason is that six categories do not
 * fit comfortably in the warm half of the wheel. It was teal (too close to
 * investment emerald), then orange — which sat only 13° from problem's amber
 * and read as the same colour at dot size, in BOTH ramps. lime takes the one
 * genuinely wide gap in the ring, 41°→155°, landing ~47° from amber and ~75°
 * from emerald. The six are now spaced roughly evenly around the wheel, which
 * is what makes them separable at a glance rather than only side by side.
 */

/** Tailwind badge classes: background, text, and border (light + dark mode). */
export const CATEGORY_BADGE: Record<string, { bg: string; text: string; border: string }> = {
  problem:     { bg: 'bg-amber-50 dark:bg-amber-900/25',   text: 'text-amber-700 dark:text-amber-300',   border: 'border-amber-200 dark:border-amber-700' },
  opportunity: { bg: 'bg-blue-50 dark:bg-blue-900/25',    text: 'text-blue-700 dark:text-blue-300',    border: 'border-blue-200 dark:border-blue-700' },
  risk:        { bg: 'bg-red-50 dark:bg-red-900/25',      text: 'text-red-700 dark:text-red-300',      border: 'border-red-200 dark:border-red-700' },
  solution:    { bg: 'bg-lime-50 dark:bg-lime-900/25',    text: 'text-lime-700 dark:text-lime-300',    border: 'border-lime-200 dark:border-lime-700' },
  commitment:  { bg: 'bg-violet-50 dark:bg-violet-900/25', text: 'text-violet-700 dark:text-violet-300', border: 'border-violet-200 dark:border-violet-700' },
  investment:  { bg: 'bg-emerald-50 dark:bg-emerald-900/25', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-200 dark:border-emerald-700' },
};

/** Tailwind dot/circle fill classes for filter panels and legends. */
export const CATEGORY_DOT: Record<string, string> = {
  problem:     'bg-amber-400',
  opportunity: 'bg-blue-400',
  risk:        'bg-red-400',
  // lime-500 rather than -400 like its peers: lime is intrinsically the
  // lightest of the six families, and -400 reads noticeably weaker than the
  // other dots. Under daylight all of 400/500/600 map to the same olive fill,
  // so this only affects the classic ramp.
  solution:    'bg-lime-500',
  commitment:  'bg-violet-400',
  investment:  'bg-emerald-400',
};

/** Hex color values for Recharts bars, canvas charts, and report charts. */
export const CATEGORY_HEX: Record<string, string> = {
  problem:     '#f59e0b', // amber-400
  opportunity: '#3b82f6', // blue-500
  risk:        '#ef4444', // red-500
  solution:    '#65a30d', // lime-600
  commitment:  '#8b5cf6', // violet-500
  investment:  '#10b981', // emerald-500
};

/**
 * Frame-picker / tile selection classes (CreateFrameModal, etc.).
 * Keeps the select-a-frame UI in lockstep with CATEGORY_BADGE.
 */
export const CATEGORY_FRAME_PICKER: Record<string, string> = {
  problem:
    'border-amber-200 hover:bg-amber-50 data-[selected=true]:bg-amber-50 data-[selected=true]:border-amber-400 data-[selected=true]:text-amber-700 dark:border-amber-700 dark:hover:bg-amber-900/20 dark:data-[selected=true]:bg-amber-900/30 dark:data-[selected=true]:border-amber-500 dark:data-[selected=true]:text-amber-300',
  opportunity:
    'border-blue-200 hover:bg-blue-50 data-[selected=true]:bg-blue-50 data-[selected=true]:border-blue-400 data-[selected=true]:text-blue-700 dark:border-blue-700 dark:hover:bg-blue-900/20 dark:data-[selected=true]:bg-blue-900/30 dark:data-[selected=true]:border-blue-500 dark:data-[selected=true]:text-blue-300',
  risk:
    'border-red-200 hover:bg-red-50 data-[selected=true]:bg-red-50 data-[selected=true]:border-red-400 data-[selected=true]:text-red-700 dark:border-red-700 dark:hover:bg-red-900/20 dark:data-[selected=true]:bg-red-900/30 dark:data-[selected=true]:border-red-500 dark:data-[selected=true]:text-red-300',
  solution:
    'border-lime-200 hover:bg-lime-50 data-[selected=true]:bg-lime-50 data-[selected=true]:border-lime-400 data-[selected=true]:text-lime-700 dark:border-lime-700 dark:hover:bg-lime-900/20 dark:data-[selected=true]:bg-lime-900/30 dark:data-[selected=true]:border-lime-500 dark:data-[selected=true]:text-lime-300',
  commitment:
    'border-violet-200 hover:bg-violet-50 data-[selected=true]:bg-violet-50 data-[selected=true]:border-violet-400 data-[selected=true]:text-violet-700 dark:border-violet-700 dark:hover:bg-violet-900/20 dark:data-[selected=true]:bg-violet-900/30 dark:data-[selected=true]:border-violet-500 dark:data-[selected=true]:text-violet-300',
  investment:
    'border-emerald-200 hover:bg-emerald-50 data-[selected=true]:bg-emerald-50 data-[selected=true]:border-emerald-400 data-[selected=true]:text-emerald-700 dark:border-emerald-700 dark:hover:bg-emerald-900/20 dark:data-[selected=true]:bg-emerald-900/30 dark:data-[selected=true]:border-emerald-500 dark:data-[selected=true]:text-emerald-300',
};

/** Fallback badge style for unknown/unset categories (light + dark mode). */
export const CATEGORY_BADGE_FALLBACK = {
  bg: 'bg-gray-50 dark:bg-gray-800',
  text: 'text-gray-600 dark:text-gray-300',
  border: 'border-gray-200 dark:border-gray-700',
};

/** Fallback dot for unknown/unset categories. */
export const CATEGORY_DOT_FALLBACK = 'bg-gray-400';

/** Fallback hex for unknown/unset categories. */
export const CATEGORY_HEX_FALLBACK = '#94a3b8'; // slate-400

/**
 * Category hex for the ramp currently on screen.
 *
 * CATEGORY_HEX above is the classic palette and stays the source of truth —
 * it is a pinned invariant and the fallback here. The daylight ramp publishes
 * `--cat-<name>` custom properties (see the generated block in src/index.css),
 * so charts follow the ramp the same way class-painted chips already do.
 *
 * Charts need a real hex: Recharts writes `fill` as an SVG *attribute*, and a
 * `var()` in an attribute does not resolve. So resolve it here at render.
 *
 * Safe before paint and outside the browser — an unset variable, a missing
 * document, or a category with no entry all fall back to the classic hex.
 */
/**
 * Category values arrive from imports, the API and hand-editing, so they are
 * not reliably normalised. Live data currently holds `Problem` alongside
 * `problem` and `Opportunity` alongside `opportunity` — one row each.
 *
 * Every map in this file is keyed lowercase, so an unnormalised value misses
 * and falls through to the grey fallback. Worse, anything that GROUPS by the
 * raw value gets a second bucket with the same display label: the item list
 * chart showed "Problem" twice, once gold and once grey.
 *
 * Normalising on read fixes display everywhere without a migration. It does
 * not clean the stored data — that is a separate decision, since collapsing
 * rows is not reversible.
 */
export function normalizeCategory(category: string | null | undefined): string {
  return (category ?? '').trim().toLowerCase();
}

export function resolveCategoryHex(category: string): string {
  const key = normalizeCategory(category);
  const classic = CATEGORY_HEX[key] ?? CATEGORY_HEX_FALLBACK;
  if (typeof document === 'undefined') return classic;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(`--cat-${key}`)
    .trim();
  return v || classic;
}

/** Every category resolved for the current ramp, in one pass. */
export function resolveCategoryHexMap(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of Object.keys(CATEGORY_HEX)) out[key] = resolveCategoryHex(key);
  return out;
}
